import {
	Component,
	HostListener,
	Input,
	OnDestroy,
	OnInit
} from '@angular/core';
import { Location } from '@angular/common';
import { ConfirmGeneralModalComponent } from '@web-components/common/confirm-general-modal/confirm-general-modal.component';
import { SNModalService } from '@web-services/modal/snModal.service';
import { zip } from 'rxjs/observable/zip';
import { QuoteUpdateService } from '@services/quote/quote-update.service';
import { ProductPackagesDataServiceProvider } from '@services/settings/product-packages.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { TemplatesDataProvider } from '@services/data-services/templates-data-service/templates.data.service';
import { PaymentSchedulesDataProvider } from '@services/payments/payments-schedules-data-service/payments-schedules.data.service';
import { debounceTime, filter, finalize, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import {
	QtBaseData,
	QtIntroductionPayload,
	QtItemPayload,
	QTSaveDataPayload,
	QtTemplateAction,
	QtTemplateType
} from '@models/qt-templates.model';
import {
	Contract,
	ContractPayload,
	CurrencyAndTaxes,
	IModalAction,
	ListItem,
	Product,
	ServerDataResponse
} from '@models/models';
import { SettingsProvider } from '@services/settings/settings';
import { of } from 'rxjs/observable/of';
import { Observable } from 'rxjs/Observable';
import { EditContractModalComponent } from '@web-components/contract/edit-contract-modal/edit-contract-modal.component';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import { diff } from 'deep-object-diff';
import { SaveAsTemplateModalComponent } from '@web-components/quotes/modals/save-as-template-modal/save-as-template-modal.component';
import { SaveNewTemplateModalComponent } from '@web-components/quotes/modals/save-new-template-modal/save-new-template-modal.component';
import { ContractsDataProvider } from '@services/contract/contracts.data.service';
import { InvoiceServiceProvider } from '@services/invoice/invoice-service/invoice.service';
import { Subscription } from 'rxjs/Subscription';
import { ContractsServiceProvider } from '@services/contract/contracts-service/contracts.service';
import { QuoteInvoiceTemplatesDataService } from '@services/quote-invoice-templates/quote-invoice-templates.data.service';
import { QuoteInvoiceTemplatesService } from '@services/quote-invoice-templates/quote-invoice-templates.service';

@Component({
	selector: 'qt-templates-update',
	template: require('./qt-templates-update.component.html'),
	styles: [require('./qt-templates-update.component.scss')]
})
export class QtTemplatesUpdateComponent implements OnInit, OnDestroy {
	@Input() templateId: string;
	@Input() templateType: QtTemplateType;
	@Input() templateAction: QtTemplateAction;

	initialTemplatesData: Partial<QtBaseData>;
	templatesData: QtBaseData;
	dataLoaded: boolean;
	disableContractButton: boolean;
	disableGenerateInvoice: boolean;
	savingTemplate: boolean;
	private loadedContractToEdit: Contract;
	private changesSaved: boolean;
	private unsubscribe: Subject<void> = new Subject();

	constructor(
		public location: Location,
		private modalService: SNModalService,
		private messageService: MessageService,
		private productPackagesDataService: ProductPackagesDataServiceProvider,
		private templatesDataService: TemplatesDataProvider,
		private paymentsSchedulesDataService: PaymentSchedulesDataProvider,
		private settingsService: SettingsProvider,
		private quoteInvoiceTemplatesDataService: QuoteInvoiceTemplatesDataService,
		private quoteInvoiceTemplatesService: QuoteInvoiceTemplatesService,
		private quoteUpdateService: QuoteUpdateService,
		private contractsDataService: ContractsDataProvider,
		private contractsService: ContractsServiceProvider
	) {}

	ngOnInit(): void {
		this.prepareBaseData();
		this.loadPageData();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	@HostListener('window:beforeunload', ['$event'])
	unloadNotification($event): void {
		if (this.initialTemplatesData && this.hasDataChanged()) {
			$event.returnValue = true;
		}
	}

	goBack(): void {
		if (this.initialTemplatesData && this.hasDataChanged()) {
			this.showConfirmExitModal();
			return;
		}
		this.goToTemplatesPage();
	}

	saveTemplate(): void {
		this.templatesData.data.products = this.quoteInvoiceTemplatesService.mapLineItems(
			this.gatherProductsData()
		);
		if (!this.isTemplatesValid(this.templatesData)) {
			return;
		}

		const data = this.quoteInvoiceTemplatesService.prepareTemplateForSave(
			this.templatesData
		);

		if (this.templateType === 'QUOTE') {
			data.data.products = QuoteUpdateService.setFixedQuoteProducts(
				data.data.products
			).map(el => {
				el.mandatory = true;
				return el;
			});
		}

		if (this.templateAction === 'DUPLICATE' || this.templateAction === 'ADD') {
			this.createTemplate(data);
			return;
		}

		if (this.templateId) {
			this.editTemplate(data, this.templateId);
		}
	}

	createTemplate(data: QTSaveDataPayload): void {
		this.savingTemplate = true;
		this.quoteInvoiceTemplatesDataService
			.saveTemplate(this.templateType, data)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): boolean => (this.savingTemplate = false))
			)
			.subscribe(
				(): void => {
					this.changesSaved = true;
					this.goToTemplatesPage();
					this.messageService.showToaster(
						this.templateAction === 'DUPLICATE' ? 's_2' : 's_0',
						'quoteInvoiceTemplates',
						this.templateType === 'INVOICE' ? 'invoice' : 'quote'
					);
				},
				(error): void => this.messageService.handleError(error)
			);
	}

	editTemplate(data: QTSaveDataPayload, id: string): void {
		this.savingTemplate = true;
		this.quoteInvoiceTemplatesDataService
			.editTemplate(this.templateType, id, data)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): boolean => (this.savingTemplate = false))
			)
			.subscribe(
				(): void => {
					this.changesSaved = true;
					this.goToTemplatesPage();
					this.messageService.showToaster(
						's_1',
						'quoteInvoiceTemplates',
						this.templateType === 'INVOICE' ? 'invoice' : 'quote'
					);
				},
				(error): void => this.messageService.handleError(error)
			);
	}

	loadContractToEdit(id: string): void {
		this.templatesData.contractTemplateId = id;
		this.loadedContractToEdit = null;

		if (id === '-1') {
			return;
		}

		this.disableContractButton = true;
		this.templatesDataService
			.getTemplates('contract_full', Number(id))
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): boolean => (this.disableContractButton = false))
			)
			.subscribe(
				(data: Contract): Contract => (this.loadedContractToEdit = data)
			);
	}

	editContractTemplate(): void {
		const modalRef = this.modalService.openModal(EditContractModalComponent, {
			windowClass: 'ng-bootstrap-modal modal-visible'
		});
		modalRef.componentInstance.isFixed = this.templateType !== 'CUSTOM_QUOTE';
		modalRef.componentInstance.contractToEdit = JSON.parse(
			JSON.stringify(this.loadedContractToEdit)
		);
		modalRef.componentInstance.action = 'SAVE_CONTRACT';

		const originalText = this.loadedContractToEdit.text;

		this.subscribeToModalData(modalRef, originalText);
	}

	updateGenerateInvoiceDisabled(paymentId) {
		this.disableGenerateInvoice = +paymentId === -3;
	}

	private subscribeToModalData(
		modalRef: NgbModalRef,
		originalText: string
	): void {
		const modalDataSubscription = this.modalService
			.getModalData()
			.subscribe((data: IModalAction<Contract>): void => {
				if (data) {
					const { action, props } = data;

					if (action === 'CLOSE_MODAL') {
						this.modalService.closeModalByRef(modalRef, modalDataSubscription);
					}
					if (action === 'SAVE_CONTRACT') {
						if (this.isTextDiffed(originalText, props.text)) {
							if (!this.contractsService.contractTextValid(props.text)) {
								return;
							}

							this.confirmEditingContract(
								props,
								modalRef,
								modalDataSubscription
							);
							return;
						}
						this.modalService.closeModalByRef(modalRef, modalDataSubscription);
					}
				}
			});
	}

	private confirmEditingContract(
		props: Contract,
		editedModalRef: NgbModalRef,
		editedModalStream: Subscription
	): void {
		if (props) {
			const modal = this.modalService.openModal(SaveAsTemplateModalComponent, {
				windowClass: 'ng-bootstrap-modal'
			});
			modal.componentInstance.templateList = this.templatesData.contracts;
			modal.componentInstance.templateTypeName = 'Contract';
			modal.componentInstance.breadcrumbsText =
				'Contract Templates will be saved in Settings > Contract Templates';
			modal.componentInstance.detailedDescription =
				'Your contract has been edited and will need to be saved as a Contract Template.';

			const modalSubscription$ = this.modalService.modalData.subscribe(
				(res: IModalAction<ListItem>): void => {
					if (res.action === 'CLOSE') {
						this.modalService.closeModalByRef(modal, modalSubscription$);
					}

					if (res.action === 'SAVE') {
						this.modalService.closeModalByRef(modal, modalSubscription$);
						this.saveNewContractTemplate(
							props,
							editedModalRef,
							editedModalStream
						);
					}

					if (res.action === 'OVERWRITE') {
						if (!res.props) {
							this.messageService.showToaster('e_2', 'quoteInvoiceTemplates');
							return;
						}

						modal.componentInstance.btnDisable = true;
						this.contractsDataService
							.overwriteContractTemplate(
								res.props.templateId.toString(),
								this.assignEditedContractData({
									...props,
									name: res.props.name
								})
							)
							.pipe(
								takeUntil(this.unsubscribe),
								finalize(
									(): boolean => (modal.componentInstance.btnDisable = false)
								)
							)
							.subscribe(
								({ data }): void => {
									this.messageService.showToaster(
										's_2',
										'contractTemplates',
										props.name
									);
									this.loadedContractToEdit = JSON.parse(JSON.stringify(props));
									this.templatesData.contractTemplateId = data.id.toString();

									this.modalService.closeModalByRef(
										editedModalRef,
										editedModalStream
									);
									this.modalService.closeModalByRef(modal, modalSubscription$);
								},
								(error): void => this.messageService.handleError(error)
							);
					}
				}
			);
		}
	}

	private assignEditedContractData(props: Contract): ContractPayload {
		return {
			name: props.name,
			optInConsent:
				(this.getNonFalsyValue(props.optInConsentStatement) as string) ||
				(this.getNonFalsyValue(props.optInConsent) as string),
			showOptInConsent:
				(this.getNonFalsyValue(props.showOtpInConsent) as boolean) ||
				(this.getNonFalsyValue(props.showOptInConsent) as boolean),
			text: props.text
		};
	}

	private saveNewContractTemplate(
		contract: Contract,
		contractEditModal: NgbModalRef,
		contractEditStream: Subscription
	): void {
		const modal = this.modalService.openModal(SaveNewTemplateModalComponent, {
			windowClass: 'ng-bootstrap-modal'
		});
		modal.componentInstance.templateTypeName = 'Contract';
		modal.componentInstance.breadcrumbsText =
			'Contract Templates will be saved in Settings > Contract Templates';
		modal.componentInstance.templateNameProp = contract.name;

		const modalSubscription$ = this.modalService.modalData.subscribe(
			(res: IModalAction<string>): void => {
				if (res.action === 'CLOSE') {
					this.confirmEditingContract(
						contract,
						contractEditModal,
						contractEditStream
					);
					this.modalService.closeModalByRef(modal, modalSubscription$);
				}

				if (res.action === 'SAVE') {
					if (!res.props || (res.props && !res.props.trim())) {
						this.messageService.showToaster('e_3', 'quoteInvoiceTemplates');
						return;
					}

					const newContract = JSON.parse(JSON.stringify(contract));
					newContract.name = res.props;

					modal.componentInstance.btnDisable = true;
					this.contractsDataService
						.saveNewContractTemplate(this.assignEditedContractData(newContract))
						.pipe(
							takeUntil(this.unsubscribe),
							finalize(
								(): boolean => (modal.componentInstance.btnDisable = false)
							)
						)
						.subscribe(
							({ data }): void => {
								this.modalService.closeModalByRef(
									contractEditModal,
									contractEditStream
								);
								this.modalService.closeModalByRef(modal, modalSubscription$);

								this.messageService.showToaster(
									's_5',
									'quoteInvoiceTemplates',
									newContract.name
								);
								this.updateContractTemplateList();
								this.templatesData.contractTemplateId = data.id.toString();
								this.loadedContractToEdit.text = newContract.text;
								this.loadedContractToEdit.name = newContract.name;
							},
							(error): void => this.messageService.handleError(error)
						);
				}
			}
		);
	}

	private updateContractTemplateList(): void {
		this.templatesDataService
			.getTemplates('contract_preview')
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(({ contractTemplates }): void => {
				this.templatesData.contracts = [...contractTemplates];
			});
	}

	private isTextDiffed(original: string, newText: string): boolean {
		return !!Object.keys(diff({ text: original }, { text: newText })).length;
	}

	private getIntroductionIfCreate(): Observable<
		ServerDataResponse<QtIntroductionPayload>
	> {
		if (!this.templateId && this.templateType !== 'INVOICE') {
			return this.quoteInvoiceTemplatesDataService.getQuoteIntroduction();
		}
		return of(null);
	}

	private loadPageData(): void {
		zip(
			this.productPackagesDataService.getPackagesOrProducts(
				QuoteUpdateService.getPageRequest()
			),
			this.templatesDataService.getTemplates('contract_preview'),
			this.templatesDataService.getTemplates('questionnaire_preview'),
			this.paymentsSchedulesDataService.getPaymentsSchedules(
				QuoteUpdateService.getPaymentSchedulesPageRequest()
			),
			this.getIntroductionIfCreate(),
			this.settingsService.getCurrencyAndTaxes(),
			this.loadQuoteIfEdit()
		)
			.pipe(
				takeUntil(this.unsubscribe),
				map(
					([
						productItems,
						contracts,
						questionnaires,
						paymentSchedules,
						introductions,
						currency,
						qtTemplate
					]): [
						Array<Product>,
						Array<ListItem>,
						Array<ListItem>,
						Array<ListItem>,
						QtIntroductionPayload | null,
						CurrencyAndTaxes,
						ServerDataResponse<QtItemPayload | null>
					] => [
						productItems.items,
						contracts.contractTemplates,
						questionnaires.questionnaireTemplates,
						paymentSchedules.results,
						introductions ? introductions.data : introductions,
						{
							currencyDTO: currency.currencyDTO,
							taxName: currency.taxName,
							percentage: currency.percentage
						},
						qtTemplate
					]
				),
				finalize((): boolean => (this.dataLoaded = true))
			)
			.subscribe(
				(data): void => {
					this.templateId
						? this.assignEditTemplatesData(data)
						: this.assignNewTemplatesData(data);

					this.saveInitialDataSnapshot();
				},
				(error): void => this.messageService.handleError(error)
			);
	}

	private loadQuoteIfEdit(): Observable<ServerDataResponse<QtItemPayload>> {
		if (this.templateId) {
			return this.quoteInvoiceTemplatesDataService.loadQtTemplate(
				this.templateId,
				this.templateType === 'CUSTOM_QUOTE' ? 'QUOTE' : this.templateType
			);
		}
		return of(null);
	}

	private assignNewTemplatesData([
		productItems,
		contracts,
		questionnaires,
		paymentSchedules,
		introductions,
		currency
	]: [
		Array<Product>,
		Array<ListItem>,
		Array<ListItem>,
		Array<ListItem>,
		QtIntroductionPayload | null,
		CurrencyAndTaxes,
		ServerDataResponse<QtItemPayload | null>
	]): void {
		const {
			packages,
			addons,
			discounts
		} = QuoteUpdateService.setPackagesAndAddons(productItems);
		this.templatesData.products = {
			items: [],
			templates: addons
		};
		this.templatesData.packages = {
			items: [QuoteUpdateService.setEmptyProduct('PACKAGE')],
			templates: packages
		};
		this.templatesData.discounts = {
			items: [],
			templates: discounts
		};
		const newAddon = QuoteUpdateService.setEmptyProduct('ADD_ON');
		if (this.templateType === 'QUOTE') {
			newAddon.mandatory = true;
		}
		this.templatesData.addons = {
			items: [newAddon],
			templates: [...packages, ...addons]
		};
		this.templatesData.data.generateInvoiceWhenAccepted = true;
		this.templatesData.contracts = contracts;
		this.templatesData.questionnaires = questionnaires;
		this.templatesData.paymentDefaultScheduleTemplates = [
			{
				id: -2,
				name: 'No split payments',
				disabled: false
			}
		];
		if (this.templateType !== 'INVOICE') {
			this.templatesData.paymentDefaultScheduleTemplates.push({
				id: -3,
				name: 'No payment required',
				disabled: false
			});
		}
		this.templatesData.paymentScheduleTemplates = paymentSchedules;
		this.templatesData.currency = currency;
		this.templatesData.summary = null;
		if (this.templateType !== 'INVOICE') {
			if (this.templateType === 'CUSTOM_QUOTE') {
				this.templatesData.data.introduction = introductions.chooseAndPickIntroduction
					? introductions.chooseAndPickIntroduction.introduction
					: '';
			} else {
				this.templatesData.data.introduction = introductions.fixedIntroduction
					? introductions.fixedIntroduction.introduction
					: '';
			}
		}
	}

	private assignEditTemplatesData([
		productItems,
		contracts,
		questionnaires,
		paymentSchedules,
		_,
		currency,
		qtTemplate
	]: [
		Array<Product>,
		Array<ListItem>,
		Array<ListItem>,
		Array<ListItem>,
		QtIntroductionPayload | null,
		CurrencyAndTaxes,
		ServerDataResponse<QtItemPayload | null>
	]): void {
		const { data } = qtTemplate;
		const {
			packages,
			addons,
			discounts
		} = QuoteUpdateService.setPackagesAndAddons(productItems);
		const lineItems = QuoteUpdateService.setPackagesAndAddons(data.products);
		this.templatesData.products = {
			items: [...lineItems.addons],
			templates: addons
		};
		this.templatesData.packages = {
			items: [...lineItems.packages],
			templates: packages
		};
		this.templatesData.discounts = {
			items: [...lineItems.discounts],
			templates: discounts
		};
		this.templatesData.addons = {
			items: [...lineItems.packages, ...lineItems.addons],
			templates: [...packages, ...addons]
		};
		this.templatesData.contracts = contracts;
		this.templatesData.contractTemplateId = data.contractTemplate
			? data.contractTemplate.id.toString()
			: '-1';

		this.templatesData.questionnaires = questionnaires;
		this.templatesData.questionnaireTemplateId = data.questionnaireTemplate
			? data.questionnaireTemplate.id.toString()
			: '-1';

		this.templatesData.paymentDefaultScheduleTemplates = [
			{
				id: -2,
				name: 'No split payments',
				disabled: false
			}
		];
		if (this.templateType !== 'INVOICE') {
			this.templatesData.paymentDefaultScheduleTemplates.push({
				id: -3,
				name: 'No payment required',
				disabled: false
			});
		}
		this.templatesData.paymentScheduleTemplates = paymentSchedules;

		this.getPaymentSchedule(data, paymentSchedules);

		this.templatesData.data.notes = data.notes;
		this.templatesData.data.introduction = data.introduction;
		if (this.templatesData.paymentSchedulerTemplateId === '-3') {
			this.disableGenerateInvoice = true;
		}
		this.templatesData.data.generateInvoiceWhenAccepted =
			this.templatesData.paymentSchedulerTemplateId === '-3' &&
			data.generateInvoiceWhenAccepted;
		this.templatesData.currency = currency;
		this.templatesData.summary = data.summary;
		this.templatesData.data.name =
			this.templateAction === 'DUPLICATE' ? `${data.name} copy` : data.name;

		this.loadedContractToEdit = data.contractTemplate;
	}

	private saveInitialDataSnapshot(): void {
		const streamRef$ = this.quoteUpdateService.updatedProductObservable
			.pipe(
				filter((product): boolean => product !== null),
				debounceTime(500)
			)
			.subscribe((): void => {
				this.initialTemplatesData = JSON.parse(
					JSON.stringify(this.templatesData)
				);
				streamRef$.unsubscribe();
			});
	}

	private hasDataChanged(): boolean {
		const diffedData = diff(
			this.initialTemplatesData,
			JSON.parse(JSON.stringify(this.templatesData))
		);
		if (Object.keys(diffedData).length) {
			return true;
		}
		return false;
	}

	private getPaymentSchedule(data, paymentSchedules) {
		const rule = data.paymentsRule;
		if (rule) {
			switch (rule.strategy) {
				case 'NO_SPLIT_PAYMENTS':
					this.templatesData.paymentSchedulerTemplateId = '-2';
					break;
				case 'NO_DUE_DATE':
					this.templatesData.paymentSchedulerTemplateId = '-3';
					break;
				case 'BY_PAYMENT_SCHEDULER':
					const paymentScdl = rule.paymentSchedule;
					if (paymentScdl) {
						const check = paymentSchedules.some(el => el.id === paymentScdl.id);
						if (!check) {
							this.templatesData.paymentScheduleTemplates.push({
								...paymentScdl
							});
						}
						this.templatesData.paymentSchedulerTemplateId = paymentScdl.id.toString();
					}
					break;
				default:
					this.templatesData.paymentSchedulerTemplateId = '-2';
			}
		} else {
			if (data.paymentSchedule) {
				const check = paymentSchedules.some(
					el => el.id === data.paymentSchedule.id
				);
				if (!check) {
					this.templatesData.paymentScheduleTemplates.push({
						...data.paymentSchedule
					});
				}
			}
			this.templatesData.paymentSchedulerTemplateId = data.paymentSchedule
				? data.paymentSchedule.id.toString()
				: '-2';
		}
	}

	private isTemplatesValid(data: QtBaseData): boolean {
		if (this.templateType === 'INVOICE') {
			return this.quoteInvoiceTemplatesService.validateInvoiceTemplate(data);
		}

		return this.quoteInvoiceTemplatesService.validateQuoteTemplate(data);
	}

	private gatherProductsData(): Array<Product> {
		if (this.templateType === 'CUSTOM_QUOTE') {
			return [
				...this.templatesData.products.items,
				...this.templatesData.packages.items,
				...this.templatesData.discounts.items
			];
		}

		return [
			...this.templatesData.addons.items,
			...this.templatesData.discounts.items
		];
	}

	private prepareBaseData(): void {
		this.templatesData =
			this.templateType === 'INVOICE'
				? InvoiceServiceProvider.getQtBaseTemplateData()
				: QuoteUpdateService.getQtBaseTemplateData();
	}

	private goToTemplatesPage(): void {
		this.location.go('/settings/quote_invoice_templates');
	}

	private showConfirmExitModal(): void {
		const modal = this.modalService.openModal(ConfirmGeneralModalComponent);
		modal.componentInstance.text = `You have unsaved changes in your ${
			this.templateType === 'INVOICE' ? 'Invoice' : 'Quote'
		} Template.`;
		modal.componentInstance.buttons = [
			{
				text: 'Save and Exit',
				color: 'success',
				size: 'lg',
				action: 'SAVE_AND_LEAVE'
			},
			{
				text: "Exit and Don't Save",
				color: 'grey',
				size: 'lg',
				action: 'LEAVE'
			},
			{
				text: 'Continue Editing',
				color: 'default',
				size: 'lg',
				action: 'STAY'
			}
		];

		modal.result.then((action: string): void => {
			if (action === 'LEAVE') {
				this.goToTemplatesPage();
			}

			if (action === 'SAVE_AND_LEAVE') {
				this.saveTemplate();
			}
		});
	}

	private getNonFalsyValue(
		value: string | boolean | null | undefined
	): string | boolean | null {
		if (typeof value === 'boolean' || typeof value === 'string') {
			return value;
		}

		if (value === undefined) {
			return null;
		}

		return null;
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/settings/quote-invoice-templates/qt-templates-update/qt-templates-update.component.ts